/**
 * -----------------------------------------------------------------------------
 * Group Detail Page
 * -----------------------------------------------------------------------------
 * This page is used to show the details of a single group of the organization.
 */

import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";
import {
  useDeleteSingleGroupMutation,
  useOrganizationGroupsQuery,
  useOrganizationVehiclesQuery,
} from "../../../api/network/adminApiServices";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import {
  setListingQueryParams,
  setModalsData,
  TListingQueryParams,
  TModalsState,
} from "../../../api/store/commonSlice";
import { TLegacyGroup } from "../../../api/types/Group";
import { LegacyVehicleEntityListingPayload } from "../../../api/types/Vehicle";
import svgIcons from "../../../assets/svg/svgIcons";
import {
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import HeaderView from "../../../components/admin/headerView";
import ModalDeleteConfirmation from "../../../components/admin/modalDeleteConfirmation";
import AdminListingColumnItem from "../../../components/adminListingColumnItem";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import AppSearchBox from "../../../components/searchBox";
import { APP_CONFIG } from "../../../constants/constants";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { routeUrls } from "../../../navigation/routeUrls";
import { useLoggedInUserData } from "../../../utils/user";
import { ROLE_ACCESS_LEVELS } from "../role/constants";
import GroupEditFormFields from "./groupEditFormFields";

const ScreenAdminDetailGroup = () => {
  const { groupId } = useParams<{ groupId: any }>();
  const { state: locationState } = useLocation(); // OrganizationEntityListingPayload | { new: true }
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.groups.detailsPage",
  });
  const { t: tMain } = useTranslation();
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { groups: orgGroupsQueryParams } = listingQueryParams;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { getUserACLAccessLevel } = useACL();
  const userHasWriteAccess =
    getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_GROUP) >=
    ROLE_ACCESS_LEVELS.WRITE;

  // flag to idenfiy if group is coming from create new group popup
  const isNewEntity = useRef<boolean>(!!locationState?.new);

  // flag to enable edit mode
  const [userCanEdit, setUserCanEdit] = React.useState<boolean>(
    !!isNewEntity?.current
  );

  // search text for right side
  const [rightSearchText, setRightSearchText] = React.useState<string>("");

  // current description and group mechainism
  const [currentDescription, setCurrentDescription] =
    React.useState<string>("");
  const [currentGroupName, setCurrentGroupName] = React.useState<string>("");

  // show inactive vehicles mechainism
  const [showInactiveVehicles, setShowInactiveVehicles] = React.useState(false);
  // show inactive users mechanism
  const [showInactiveUsers, setShowInactiveUsers] = React.useState(false);

  // prepare query params for fetching organization groups
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const [orgVehiclesQueryParams, setOrgVehiclesQueryParams] =
    React.useState<LegacyVehicleEntityListingPayload>({
      accountid: `${thisUserOrganizationId}`,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.LARGE_PAGE_SIZE, // TODO: all vehicles should be fetched page-by-page sequencially
      search: "",
      //isactive: "both" as FilterType // not needed as both active and inactive vehicles are needed
    });

  // fetch organization groups
  const {
    data: dataOrgGroups,
    isFetching: isFetchingOrgGroups,
    error,
  } = useOrganizationGroupsQuery(orgGroupsQueryParams, {
    refetchOnMountOrArgChange: true, // (WIP Aug 13, 2024) facing issue with cache, so refetching on mount
  });

  // fetch all organization vehicles
  const {
    data: dataOrgVehicles,
    isFetching: isFetchingOrgVehicles,
    isUninitialized: isUninitializedOrgVehicles,
  } = useOrganizationVehiclesQuery({
    ...orgVehiclesQueryParams,
    // 'search_fields' intentionally not added here as default search fields are defined in the API service
  });
  const { results: dataOrgVehicleResults } = dataOrgVehicles ?? {};
  const { results, count } = dataOrgGroups ?? {};

  // prepare list data for group list
  const listData: TLegacyGroup[] = !!results ? results : [];

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    setOrgVehiclesQueryParams({ ...orgVehiclesQueryParams, search: value });
  }, 500);

  const [deleteSingleGroupApiTrigger, { isLoading: isLoadingDeleteGroup }] =
    useDeleteSingleGroupMutation();

  // handle delete group
  const handleDeleteGroup = () => {
    deleteSingleGroupApiTrigger({
      accountid: `${thisUserOrganizationId}`,
      groupid: groupId,
    })
      .unwrap()
      .then(() => {
        setCurrentDescription("");
        toast.success(t("toast.group_deleted"));
        navigate(routeUrls.dashboardChildren.adminChildren.groups);
      })
      .catch((error: any) => {
        const errors = !!error?.data
          ? serializeErrorKeyValues(error?.data)
          : [t("toast.deletion_failed")];
        toast.error(errors?.join(" "));
      });
  };

  // loading states
  const [isLoadingEditGroup, setIsLoadingEditGroup] = React.useState(false);
  const [isFetchingSingleGroup, setIsFetchingSingleGroup] =
    React.useState(false);
  const [isFetchingSingleGroupVehicles, setIsFetchingSingleGroupVehicles] =
    React.useState(false);

  return (
    <>
      <HeaderView
        title={t("heading")}
        showBackButton={true}
        backButtonCallback={() =>
          navigate(routeUrls.dashboardChildren.adminChildren.groups)
        }
      />
      {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
        <pre>
          isNewEntity: {JSON.stringify(isNewEntity, null, 2)}
          <br />
          userCanEdit: {JSON.stringify(userCanEdit, null, 2)}
        </pre>
      )}
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2`}>
        <div className="lg:grid lg:grid-cols-12 py-2 gap-4">
          <div
            className={`hidden xl:block lg:col-span-3 space-y-4${isFetchingOrgGroups ? " opacity-40 pointer-events-none" : ""}${isNewEntity?.current ? " xl:hidden" : ""} relative flex flex-col`}
          >
            <div className="font-bold text-lg leading-6 mt-2 mb-3">
              {t("listing_heading")}
            </div>
            <AppSearchBox
              placeholder={t("search_placeholder_group")}
              initialValue={orgGroupsQueryParams.search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                debouncedSetSearchKeyword(e.target.value)
              }
              onClear={() => {
                debouncedSetSearchKeyword("");
              }}
            />
            <div className="max-h-admins overflow-y-auto">
              {listData?.map((item, index: number) => {
                const totalAssets =
                  item.active_inactive_vehicle_counts.active_vehicle_count +
                  item.active_inactive_vehicle_counts.inactive_vehicle_count;
                return (
                  <AdminListingColumnItem
                    key={index}
                    selected={groupId === item.groupid}
                    onClick={() =>
                      navigate(
                        `${routeUrls.dashboardChildren.adminChildren.groups}/${item.groupid}`
                      )
                    }
                    title={`${item.groupid}`}
                    description={`${item.description}`}
                    asideText={
                      item.active_inactive_vehicle_counts.active_vehicle_count >
                      0
                        ? t(
                            item.active_inactive_vehicle_counts
                              .active_vehicle_count === 1
                              ? "active_count_1"
                              : "active_count",
                            {
                              count:
                                item.active_inactive_vehicle_counts
                                  .active_vehicle_count,
                            }
                          )
                        : ""
                    } // ${item.is_active ? tMain("admins.filters.active") : tMain("admins.filters.inactive")}`}
                    // bottomText={item.vehicles.length === 1 ? t('vehicle_count_1') : t('vehicle_count', { count: item.vehicles.length})}
                    bottomText={t(
                      totalAssets === 1 ? "vehicle_count_1" : "vehicle_count",
                      { count: totalAssets }
                    )} // TODO: fix the line above and remove this line
                  />
                );
              })}
            </div>
            {!isFetchingOrgGroups && (
              <Pagination
                pageSize={orgGroupsQueryParams.page_size}
                handlePageSizeChange={(e: TSelectboxOption | null) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      groups: {
                        ...orgGroupsQueryParams,
                        page: 1,
                        page_size: parseInt(`${e?.value}`),
                      },
                    })
                  );
                }}
                totalPages={
                  count ? Math.ceil(count / orgGroupsQueryParams.page_size) : 1
                }
                forcePage={orgGroupsQueryParams.page - 1}
                handlePageClick={(data: TPaginationSelected) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      groups: {
                        ...orgGroupsQueryParams,
                        page: data?.selected + 1,
                      },
                    })
                  );
                }}
                onlyPageChange={true}
                size={1}
              />
            )}
          </div>
          <div
            className={`${isNewEntity?.current ? "lg:col-span-12" : "col-span-12 xl:col-span-9"} space-y-4`}
          >
            <div className="flex items-center gap-4">
              {isNewEntity?.current ? (
                <>
                  <p className="font-bold text-lg leading-6">
                    {currentGroupName}
                  </p>
                  <div className="flex-grow"></div>
                  {(isFetchingSingleGroup || isFetchingSingleGroupVehicles) && (
                    <img
                      src={svgIcons.LoadingAnimation}
                      alt="loading-animation"
                      className="w-8 h-8"
                    />
                  )}
                  <p className="font-semibold text-accent-blue-dark text-base leading-6">
                    {tMain("admins.completeCreation")}
                  </p>
                  {/* <div className="w-24">
                      <AdminFormFieldSubmit
                        type="submit"
                        variant="success"
                        label={tMain("save")}
                        onClick={handleEditFleettag}
                        disabled={isLoadingEditFleettag}
                      />
                    </div> */}
                </>
              ) : (
                <>
                  <p className="font-bold text-lg leading-6">
                    {currentGroupName}
                  </p>
                  <div className="flex-grow"></div>
                  {(isFetchingSingleGroup || isFetchingSingleGroupVehicles) && (
                    <img
                      src={svgIcons.LoadingAnimation}
                      alt="loading-animation"
                      className="w-8 h-8"
                    />
                  )}
                  {/* Removed as this is not needed for now
                  <div className="w-24">
                    <AdminFormFieldSubmit
                      type="button"
                      variant="primary"
                      label={tMain("help")}
                      onClick={() => { }}
                      disabled={isLoadingEditGroup}
                    />
                  </div> */}
                  {getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_GROUP) >=
                    ROLE_ACCESS_LEVELS.WRITE && (
                    <div className="w-24">
                      <AdminFormFieldSubmit
                        type="button"
                        variant="primary"
                        label={tMain("edit")}
                        onClick={() => setUserCanEdit(!userCanEdit)}
                        disabled={
                          userCanEdit ||
                          isLoadingEditGroup ||
                          isFetchingSingleGroup ||
                          isFetchingSingleGroupVehicles
                        }
                      />
                    </div>
                  )}
                  {getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_GROUP) >=
                    ROLE_ACCESS_LEVELS.ALL && (
                    <div className="w-24">
                      <AdminFormFieldSubmit
                        type="button"
                        variant="danger"
                        label={tMain("delete")}
                        onClick={() => {
                          dispatch(
                            setModalsData({
                              ...modalsState,
                              showDeleteConfirmation: true,
                            })
                          );
                        }}
                        disabled={
                          isLoadingEditGroup ||
                          isFetchingSingleGroup ||
                          isFetchingSingleGroupVehicles
                        }
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            <GroupEditFormFields
              groupId={groupId}
              loadingState={isLoadingDeleteGroup}
              isNewEntity={isNewEntity}
              onCancel={() => {
                if (isNewEntity?.current) {
                  navigate(routeUrls.dashboardChildren.adminChildren.groups);
                } else {
                  // already handled inside the component
                  // setUserCanEdit(false);
                }
              }}
              onLoadingStateChangeEditGroup={setIsLoadingEditGroup}
              onLoadingStateChangeSingleGroup={setIsFetchingSingleGroup}
              onLoadingStateChangeSingleGroupVehicles={
                setIsFetchingSingleGroupVehicles
              }
              onUpdateError={(error) => {
                const errors = !!error?.data
                  ? serializeErrorKeyValues(error?.data)
                  : [t("toast.updation_failed")];
                toast.error(errors?.join(" "));
              }}
              onUpdateSuccess={() => {
                toast.success(t("toast.group_updated"));
                navigate(`${routeUrls.dashboardChildren.adminChildren.groups}`);
              }}
              setShowInactiveVehicles={setShowInactiveVehicles}
              setShowInactiveUsers={setShowInactiveUsers}
              setUserCanEdit={setUserCanEdit}
              showGroupIdField={false}
              showInactiveVehicles={showInactiveVehicles}
              showInactiveUsers={showInactiveUsers}
              userCanEdit={userCanEdit}
              viewOnlyMode={!userHasWriteAccess}
            />
          </div>
        </div>
      </div>
      {/* <AdminsGroupsCreateNew /> */}
      <ModalDeleteConfirmation handleDeleteAdmin={handleDeleteGroup} />
    </>
  );
};

export default ScreenAdminDetailGroup;
